import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useMst } from '../../../models/stores/Root';
import {
  SpreedlyOnErrorsHandler,
  SpreedlyOnPaymentMethodHandler,
  SpreedlyRecacheEventHandlers,
} from '../../../types/Spreedly';
import SectionTitle from '../../content/SectionTitle';
import { SpreedlyValues, UIConstants } from '../../../utils/constants';
import ToolTip from '../../content/ToolTip';
import SpreedlyFramedInput from '../../input/SpreedlyFramedInput';

export type SpreedlyCvvOnlyProps = {
  spreedlyEventHandlers: SpreedlyRecacheEventHandlers;
};

export const SpreedlyCvvOnlyTestId = 'SpreedlyCvvOnly-payment-input';
export const SpreedlyHiddenCardFieldTestId = 'SpreedlyHiddenCard-payment-input';

const SpreedlyCvvOnly: React.FC<SpreedlyCvvOnlyProps> = observer(
  ({ spreedlyEventHandlers }: SpreedlyCvvOnlyProps) => {
    const rootStore = useMst();
    const { configuration, cart } = rootStore;
    const [isRendered, setIsRendered] = useState(false);
    const [cvvErrorLabel, setCvvErrorLabel] = useState<string | undefined>(
      undefined
    );

    const handleOnRecacheErrorLabels = (
      token: string,
      paymentMethod: spreedly.SpreedlyPaymentMethod,
      onRecacheCallback: SpreedlyOnPaymentMethodHandler
    ): void => {
      setCvvErrorLabel(undefined);
      cart.updateSpreedlyTransactionDetails({
        browserInfo:
          configuration.payments.isSpreedly3DSEnabled &&
          configuration.features.enableSpreedly
            ? // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              Spreedly.ThreeDS.serialize('03', configuration.headerAccept)
            : null,
      });
      onRecacheCallback(token, paymentMethod);
    };

    const handleOnErrorsErrorLabels = (
      errors: spreedly.SpreedlyError[],
      onErrorsCallback: SpreedlyOnErrorsHandler
    ): void => {
      setCvvErrorLabel(undefined);
      const hasCvvError = errors.filter(
        error => error.attribute?.toLowerCase() === 'cvv'
      );
      if (hasCvvError.length) {
        setCvvErrorLabel(hasCvvError[0].message);
      }
      onErrorsCallback(errors);
    };

    const wrappedEventHandlers = {
      onRecache: (
        token: string,
        paymentMethod: spreedly.SpreedlyPaymentMethod
      ) =>
        handleOnRecacheErrorLabels(
          token,
          paymentMethod,
          spreedlyEventHandlers.onRecache
        ),
      onErrors: (errors: spreedly.SpreedlyError[]) =>
        handleOnErrorsErrorLabels(errors, spreedlyEventHandlers.onErrors),
      onThreeDSStatus: spreedlyEventHandlers.onThreeDSStatus,
    };

    useEffect(() => {
      if (!isRendered) {
        rootStore.initializeSpreedlyInputs(wrappedEventHandlers);
        setIsRendered(true);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [wrappedEventHandlers]);
    return (
      <div data-testid={SpreedlyCvvOnlyTestId}>
        <SectionTitle>Credit Card Security Code</SectionTitle>
        <div
          id={SpreedlyValues.CARD_NUMBER_ID}
          className="hidden"
          data-testid={SpreedlyHiddenCardFieldTestId}
        />
        <SpreedlyFramedInput
          id={SpreedlyValues.CVV_ID}
          label="CVV"
          icon={
            <ToolTip
              tipText={UIConstants.CVV_TOOL_TIP}
              yPosition="above"
              xPosition="left"
              isInputIcon
            />
          }
          errorLabel={cvvErrorLabel}
        />
      </div>
    );
  }
);

export default SpreedlyCvvOnly;
